/**
 * useTerms() — the ONLY way a component obtains a display word.
 *
 * design.md § 2.1: Blueprint, Room, Pillar, Sliver and Deliverable, plus the
 * four deliverable state labels, are client-overridable. No component may
 * hardcode any of them — not in a heading, a label, a tooltip, a warning
 * message, an empty state, a confirmation, the page title, an aria-label or a
 * print header. One hardcoded "Room" in a warning string is exactly the defect
 * nobody catches until a client does.
 *
 * Plurals come from here too. Never append an "s": a client who renames Pillar
 * to "Phase of Analysis" has no suffixable plural.
 *
 * What this hook does NOT change: CSS class names (.s-draft and siblings),
 * STATE_ORDER and the cycle sequence, the canonical state a chip *is*, API
 * field names, and workbook sheet and column names. Only the rendered word.
 *
 * The resolution chain (system default → client → blueprint) lives in
 * `resolveTerms` in @blueprint/shared and is applied where a blueprint is
 * loaded. This provider simply carries the already-resolved set. Until a
 * blueprint is open it serves DEFAULT_TERMINOLOGY, so a component can always
 * call the hook and always get a sensible word.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  DEFAULT_TERMINOLOGY,
  type NounKey,
  type StateTermKey,
  type Terminology,
} from '@blueprint/shared';

export interface Terms {
  /** The singular noun, e.g. terms.noun('room') → "Room". */
  noun: (key: NounKey) => string;
  /** The plural noun — stored, never derived. */
  plural: (key: NounKey) => string;
  /** A deliverable state's label. States have no plural; write "3 × Draft". */
  state: (key: StateTermKey) => string;
  /** The resolved set, for the rare caller that needs to iterate. */
  all: Terminology;
}

const TermsContext = createContext<Terminology>(DEFAULT_TERMINOLOGY);

export function TermsProvider({
  terminology = DEFAULT_TERMINOLOGY,
  children,
}: {
  terminology?: Terminology;
  children: ReactNode;
}) {
  return <TermsContext.Provider value={terminology}>{children}</TermsContext.Provider>;
}

export function useTerms(): Terms {
  const t = useContext(TermsContext);
  return useMemo(
    () => ({
      noun: (key: NounKey) => t.nouns[key].singular,
      plural: (key: NounKey) => t.nouns[key].plural,
      state: (key: StateTermKey) => t.states[key].label,
      all: t,
    }),
    [t],
  );
}
